import React from 'react';
import { cx } from './cx';
import { focusRing } from './focusRing';
import { playClickSound } from '@/utils/soundUtils';

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Accessible name (becomes aria-label). */
  label: string;
  icon?: React.ReactNode;
  /** `sm` 32, `md` 40, `lg` 44, `xl` 48 design px (touch targets on the player hub screens). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'neutral' | 'ghost' | 'primary';
  silent?: boolean;
}

const SIZES = { sm: 'size-8', md: 'size-10', lg: 'size-11', xl: 'size-12' } as const;
/** Fill, text and (except ghost) the 3D edge colour that k-btn-depth draws under the button. */
const VARIANTS = {
  neutral:
    'bg-action-neutral border border-edge-subtle text-secondary hover:bg-action-neutral-hover hover:text-primary [--k-btn-edge:var(--k-btn-edge-neutral)]',
  ghost: 'bg-transparent text-secondary hover:bg-hover hover:text-primary',
  primary: 'bg-action-primary text-white hover:bg-action-primary-hover [--k-btn-edge:var(--k-btn-edge-primary)]',
} as const;

/** Square icon-only button; always has an accessible name. Corners and depth (not on ghost) come from the style recipe. */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 'md', variant = 'neutral', silent = false, disabled, onClick, className, children, type = 'button', ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        if (!disabled && !silent) playClickSound();
        onClick?.(e);
      }}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-(--k-radius-button) transition-colors duration-(--k-dur-fast)',
        variant !== 'ghost' && 'k-btn-depth',
        'disabled:cursor-not-allowed disabled:text-disabled',
        focusRing,
        SIZES[size],
        VARIANTS[variant],
        className
      )}
      {...rest}
    >
      <span className="inline-flex [&>svg]:size-4.5" aria-hidden>
        {icon ?? children}
      </span>
    </button>
  );
});

export default IconButton;
